import { type FC, useState } from "react";

import type { TraitCondition as TraitDefinitionCondition } from "@hightouch/lib/query/visual/types/trait-definitions";
import {
  Box,
  Button,
  Column,
  Dialog,
  Row,
  Text,
  TraitIcon,
  TraitTemplateIcon,
} from "@hightouch/htui";

import type { AdditionalColumn, EventCondition } from "src/types/visual";

import { AudienceEventTraitForm } from "./audience-event-trait-form";
import { CreateAudienceTraitModal } from "./create-audience-trait-modal";
import type { FullParentModel } from "./types";

type Method = "inline" | "template";

type MethodOption = {
  method: Method;
  label: string;
  description: string;
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
};

const METHOD_OPTIONS: MethodOption[] = [
  {
    method: "inline",
    label: "Create an inline trait from scratch",
    description: "Define custom aggregation and filters for this event",
    icon: TraitIcon,
  },
  {
    method: "template",
    label: "Build from a trait template",
    description: "Start from an existing trait template",
    icon: TraitTemplateIcon,
  },
];

const MethodOptionRow: FC<{
  option: MethodOption;
  onClick: () => void;
}> = ({ option, onClick }) => (
  <Row
    as="button"
    alignItems="center"
    gap={4}
    onClick={onClick}
    width="100%"
    p={4}
    borderRadius="xl"
    cursor="pointer"
    _hover={{ bg: "base.background" }}
  >
    <Box
      as={option.icon}
      color="text.secondary"
      height={6}
      width={6}
      flexShrink={0}
    />
    <Column align="flex-start">
      <Text fontWeight="semibold">{option.label}</Text>
      <Text color="text.secondary" fontWeight="normal">
        {option.description}
      </Text>
    </Column>
  </Row>
);

const MethodSelector: FC<{
  onSelect: (method: Method) => void;
  onClose: () => void;
}> = ({ onSelect, onClose }) => (
  <Dialog
    isOpen
    title="Add event trait"
    width="lg"
    actions={<Button onClick={onClose}>Cancel</Button>}
    onClose={onClose}
    variant="form"
  >
    <Column>
      {METHOD_OPTIONS.map((option) => (
        <MethodOptionRow
          key={option.method}
          option={option}
          onClick={() => onSelect(option.method)}
        />
      ))}
    </Column>
  </Dialog>
);

export const EventAudienceTraitEntrypoint: FC<{
  condition: EventCondition;
  parentModel: FullParentModel | null | undefined;
  onClose: () => void;
  onSubmit: (value: AdditionalColumn) => Promise<void>;
}> = ({ condition, parentModel, onClose, onSubmit }) => {
  const [method, setMethod] = useState<Method | null>(null);

  if (!method) {
    return <MethodSelector onSelect={setMethod} onClose={onClose} />;
  }

  if (method === "inline") {
    return (
      <AudienceEventTraitForm
        condition={condition}
        parent={parentModel}
        title="Add event trait"
        onClose={onClose}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <CreateAudienceTraitModal
      parentModel={parentModel}
      filterByRelationshipId={condition.relationshipId?.toString()}
      conditions={
        (condition.subconditions ?? []) as unknown as TraitDefinitionCondition[]
      }
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
};
